import {
  useState,
  useMemo,
  useEffect,
  useCallback,
  memo,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { FaCheckCircle, FaUser } from "react-icons/fa";
import { LuCalendarClock } from "react-icons/lu";
import { cn, destructiveOutlineBtnClass } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { HomePhaseShell, HomePhaseBadge } from "@/components/v2/home/HomePhaseShell";
import { PollHourStrip } from "@/components/v2/home/PollHourStrip";
import { HorizontalScrollStrip } from "@/components/v2/home/HorizontalScrollStrip";
import type { PollV2 } from "@/lib/api/polls";
import { buildPollV2Aggregates } from "@/lib/api/polls";
import {
  formatHourLabel,
  formatPollCountdown,
  getInitialAdminConfirm,
  hourFromSlotTime,
  suggestedSlots,
  surveyDayGridCols,
  topSlots,
} from "@/lib/v2/pollAggregates";

interface HomePollVotingCardProps {
  poll: PollV2;
  isAdmin: boolean;
  closingPoll: boolean;
  confirming: boolean;
  onVote: (
    slotIds: string[],
    options?: { onSettled?: () => void }
  ) => void;
  onConfirm: (slotId: string) => void;
  onCancel: () => void;
  headerAction?: ReactNode;
}

function PollCloseNote({ closesAt }: { closesAt: string | null }) {
  const { t } = useTranslation();
  const closesMs = closesAt ? parseISO(closesAt).getTime() : 0;
  const [remainingMs, setRemainingMs] = useState(() => (closesAt ? closesMs - Date.now() : 0));

  useEffect(() => {
    if (!closesAt) return;
    const id = setInterval(() => setRemainingMs(closesMs - Date.now()), 1000);
    return () => clearInterval(id);
  }, [closesAt, closesMs]);

  const closed = !closesAt || remainingMs <= 0;

  return (
    <HomePhaseBadge tone={closed ? "amber" : "primary"}>
      {closed
        ? t("v2.home.pollClosedShort")
        : t("v2.home.pollClosesInShort", { time: formatPollCountdown(remainingMs) })}
    </HomePhaseBadge>
  );
}

function formatDayName(date: string, locale: typeof es) {
  const name = format(parseISO(`${date}T12:00:00`), "EEEE", { locale });
  return name.charAt(0).toUpperCase() + name.slice(1);
}

function formatDayDate(date: string, locale: typeof es) {
  return format(parseISO(`${date}T12:00:00`), "d MMM", { locale });
}

function getPollHoursForDay(poll: PollV2, dayId: string): number[] {
  const day = poll.days?.find((d) => d.id === dayId);
  if (!day?.slots?.length) return [];
  return day.slots
    .map((s) => hourFromSlotTime(s.slot_time))
    .sort((a, b) => a - b);
}

function slotIdForHour(poll: PollV2, dayId: string, hour: number): string | null {
  const day = poll.days?.find((d) => d.id === dayId);
  const slot = day?.slots?.find((s) => hourFromSlotTime(s.slot_time) === hour);
  return slot?.id ?? null;
}

function DayVoterBadge({ count, total }: { count: number; total: number }) {
  const { t } = useTranslation();
  return (
    <p
      className="mt-0.5 inline-flex items-center gap-0.5 text-[10px] font-medium tabular-nums text-muted-foreground"
      title={t("v2.home.dayVotersTooltip", { count, total })}
    >
      <FaUser className="h-2.5 w-2.5 shrink-0 opacity-70" aria-hidden />
      {t("v2.home.dayVotersCount", { count })}
    </p>
  );
}

const PollDayTab = memo(function PollDayTab({
  dayId,
  day,
  active,
  dateLocale,
  voterCount,
  totalVoters,
  onSelect,
  className,
}: {
  dayId: string;
  day: string;
  active: boolean;
  dateLocale: typeof es;
  voterCount?: number;
  totalVoters: number;
  onSelect: (dayId: string) => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      data-day-id={dayId}
      aria-pressed={active}
      onClick={() => onSelect(dayId)}
      className={cn(
        "min-h-[44px] shrink-0 snap-start rounded-lg border px-3 py-2 text-left transition-transform active:scale-[0.99]",
        active
          ? "border-soccer-primary bg-soccer-primary/10"
          : "border-border bg-card hover:bg-muted/40",
        className
      )}
    >
      <p className="truncate text-sm font-semibold leading-tight">
        {formatDayName(day, dateLocale)}
      </p>
      <p className="text-xs text-muted-foreground">
        {formatDayDate(day, dateLocale)}
      </p>
      {voterCount !== undefined && (
        <DayVoterBadge count={voterCount} total={totalVoters} />
      )}
    </button>
  );
});

export function HomePollVotingCard({
  poll,
  isAdmin,
  closingPoll,
  confirming,
  onVote,
  onConfirm,
  onCancel,
  headerAction,
}: HomePollVotingCardProps) {
  const { t } = useTranslation();
  const dateLocale = es;

  const { days, aggregates, totalVoters, dayVoterCounts } = useMemo(
    () => buildPollV2Aggregates(poll),
    [poll]
  );
  const initialAdmin = useMemo(
    () => getInitialAdminConfirm(aggregates),
    [aggregates]
  );
  const defaultDayId =
    days[Math.min(1, Math.max(days.length - 1, 0))]?.id ?? days[0]?.id ?? "";

  const [selectedDayId, setSelectedDayId] = useState(defaultDayId);
  const serverSlotIdsKey = (poll.my_slot_ids ?? []).slice().sort().join(",");
  const serverSlotIds = useMemo(
    () => new Set(poll.my_slot_ids ?? []),
    [serverSlotIdsKey]
  );
  const [mySlotIds, setMySlotIds] = useState<Set<string>>(
    () => new Set(poll.my_slot_ids ?? [])
  );
  const [savingVotes, setSavingVotes] = useState(false);
  const [adminSheetOpen, setAdminSheetOpen] = useState(false);
  const [adminConfirmDayId, setAdminConfirmDayId] = useState(
    initialAdmin?.dayId ?? defaultDayId
  );
  const [adminConfirmHour, setAdminConfirmHour] = useState<number | null>(
    initialAdmin?.hour ?? null
  );

  useEffect(() => {
    setMySlotIds(new Set(poll.my_slot_ids ?? []));
    setSavingVotes(false);
  }, [poll.id, serverSlotIdsKey]);

  const selectedDay = days.find((d) => d.id === selectedDayId) ?? days[0];
  const playerHours = useMemo(
    () => (selectedDay ? getPollHoursForDay(poll, selectedDay.id) : []),
    [poll, selectedDay]
  );

  const suggested = useMemo(
    () => suggestedSlots(aggregates, totalVoters),
    [aggregates, totalVoters]
  );

  const votesBySlotId = useMemo(() => {
    const map = new Map<string, number>();
    for (const row of aggregates) map.set(row.slotId, row.votes);
    return map;
  }, [aggregates]);

  const isDirty = useMemo(() => {
    if (mySlotIds.size !== serverSlotIds.size) return true;
    for (const id of mySlotIds) {
      if (!serverSlotIds.has(id)) return true;
    }
    return false;
  }, [mySlotIds, serverSlotIds]);

  const selectDay = useCallback((dayId: string) => {
    setSelectedDayId(dayId);
  }, []);

  const toggleHour = useCallback(
    (hour: number) => {
      if (!selectedDay) return;
      const slotId = slotIdForHour(poll, selectedDay.id, hour);
      if (!slotId) return;

      setMySlotIds((prev) => {
        const next = new Set(prev);
        if (next.has(slotId)) next.delete(slotId);
        else next.add(slotId);
        return next;
      });
    },
    [poll, selectedDay]
  );

  const handleSaveVotes = () => {
    if (!isDirty || savingVotes) return;
    setSavingVotes(true);
    onVote([...mySlotIds], {
      onSettled: () => {
        setSavingVotes(false);
      },
    });
  };

  const adminConfirmDay = days.find((d) => d.id === adminConfirmDayId) ?? null;
  const adminConfirmHours = adminConfirmDay
    ? getPollHoursForDay(poll, adminConfirmDay.id)
    : [];
  const adminConfirmSlotId =
    adminConfirmDay && adminConfirmHour !== null
      ? slotIdForHour(poll, adminConfirmDay.id, adminConfirmHour)
      : null;
  const adminCanConfirm = Boolean(adminConfirmSlotId);

  const topSlot = topSlots(aggregates, 1)[0];
  const adminVoteCountsByHour = useMemo(() => {
    const map = new Map<number, number>();
    if (!adminConfirmDay) return map;
    for (const hour of adminConfirmHours) {
      const slotId = slotIdForHour(poll, adminConfirmDay.id, hour);
      if (slotId) map.set(hour, votesBySlotId.get(slotId) ?? 0);
    }
    return map;
  }, [adminConfirmDay, adminConfirmHours, poll, votesBySlotId]);

  const adminTopPickHours = useMemo(() => {
    if (!topSlot || !adminConfirmDay) return new Set<number>();
    if (topSlot.dayId !== adminConfirmDay.id) return new Set<number>();
    return new Set([topSlot.hour]);
  }, [topSlot, adminConfirmDay]);

  if (!selectedDay) return null;

  const adminConfirmSummary =
    adminConfirmDay && adminConfirmHour !== null
      ? `${formatDayName(adminConfirmDay.day, dateLocale)} ${formatHourLabel(adminConfirmHour)}`
      : null;

  const adminSheet = (
    <Sheet open={adminSheetOpen} onOpenChange={setAdminSheetOpen}>
      <SheetContent side="bottom" className="max-h-[90vh] overflow-y-auto rounded-t-2xl px-4 pt-6">
        <SheetHeader className="text-left">
          <SheetTitle>{t("v2.home.confirmConvocatoria")}</SheetTitle>
          <SheetDescription>{t("v2.home.confirmConvocatoriaDesc")}</SheetDescription>
        </SheetHeader>

        <div className="mt-4 flex flex-col gap-4">
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
            <span className="font-medium text-foreground/80">
              {t("v2.home.adminPollVoters", { count: totalVoters })}
            </span>
            {topSlot && (
              <span>
                {t("v2.home.adminPollTopPick", {
                  label: `${formatDayName(topSlot.day, dateLocale)} ${formatHourLabel(topSlot.hour)}`,
                })}
              </span>
            )}
          </div>

          <div className={cn("grid gap-2", surveyDayGridCols(days.length))}>
            {days.map((day) => {
              const active = adminConfirmDayId === day.id;
              return (
                <button
                  key={`admin-${day.id}`}
                  type="button"
                  onClick={() => {
                    setAdminConfirmDayId(day.id);
                    const hours = getPollHoursForDay(poll, day.id);
                    setAdminConfirmHour((prev) =>
                      prev !== null && hours.includes(prev) ? prev : (hours[0] ?? null)
                    );
                  }}
                  className={cn(
                    "min-h-[44px] rounded-lg border px-2.5 py-2 text-left transition-transform active:scale-[0.99]",
                    active
                      ? "border-amber-400/60 bg-amber-400/10 ring-1 ring-amber-400/25"
                      : "border-border bg-card hover:bg-muted/40"
                  )}
                >
                  <p className="truncate text-sm font-semibold leading-tight">
                    {formatDayName(day.day, dateLocale)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatDayDate(day.day, dateLocale)}
                  </p>
                  {dayVoterCounts.has(day.id) && (
                    <DayVoterBadge
                      count={dayVoterCounts.get(day.id)!}
                      total={totalVoters}
                    />
                  )}
                </button>
              );
            })}
          </div>

          {adminConfirmDay && adminConfirmHours.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {t("v2.home.confirmHourLabel")}
              </p>
              <PollHourStrip
                hours={adminConfirmHours}
                selectedHour={adminConfirmHour}
                onSelectHour={setAdminConfirmHour}
                variant="admin"
                voteCounts={adminVoteCountsByHour}
                topPickHours={adminTopPickHours}
              />
            </div>
          )}

          {adminConfirmSummary && (
            <p className="text-sm text-muted-foreground">
              {t("v2.home.convocatoriaLabel")}:{" "}
              <span className="font-semibold text-foreground">{adminConfirmSummary}</span>
            </p>
          )}

          <div className="flex flex-col gap-2 pt-2">
            <Button
              className="min-h-[48px] w-full bg-amber-600 font-bold text-white hover:bg-amber-600/90"
              disabled={!adminCanConfirm || confirming || closingPoll}
              onClick={() => {
                if (adminConfirmSlotId) {
                  onConfirm(adminConfirmSlotId);
                  setAdminSheetOpen(false);
                }
              }}
            >
              {confirming ? t("common.loading") : t("v2.home.confirmSlot")}
            </Button>
            <Button
              type="button"
              variant="outline"
              className={cn("min-h-[44px] w-full font-semibold", destructiveOutlineBtnClass)}
              disabled={closingPoll || confirming}
              onClick={onCancel}
            >
              {t("v2.home.cancelPoll")}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );

  const useDayScroll = days.length > 2;

  return (
    <>
      <HomePhaseShell
        eyebrow={t("v2.home.phaseEyebrowPollOpen")}
        title={poll.question ?? t("v2.home.pollTitle")}
        eyebrowBadge={<PollCloseNote closesAt={poll.closes_at} />}
        headerAction={headerAction}
        footer={
          isAdmin ? (
            <Button
              type="button"
              className="min-h-[48px] w-full bg-amber-600 font-bold text-white hover:bg-amber-600/90 shadow-sm"
              onClick={() => setAdminSheetOpen(true)}
            >
              <LuCalendarClock className="mr-1.5 h-4 w-4" />
              {t("v2.home.adminConfirmScheduleCta")}
            </Button>
          ) : undefined
        }
      >
        {useDayScroll ? (
          <HorizontalScrollStrip
            hint={
              <p className="text-[11px] text-muted-foreground md:hidden">
                {t("v2.home.swipeDaysHint")}
              </p>
            }
            role="tablist"
          >
            {days.map((day) => (
              <PollDayTab
                key={day.id}
                dayId={day.id}
                day={day.day}
                active={selectedDayId === day.id}
                dateLocale={dateLocale}
                voterCount={
                  dayVoterCounts.has(day.id) ? dayVoterCounts.get(day.id)! : undefined
                }
                totalVoters={totalVoters}
                onSelect={selectDay}
                className="min-w-[5.5rem]"
              />
            ))}
          </HorizontalScrollStrip>
        ) : (
          <div className={cn("grid gap-2", surveyDayGridCols(days.length))}>
            {days.map((day) => (
              <PollDayTab
                key={day.id}
                dayId={day.id}
                day={day.day}
                active={selectedDayId === day.id}
                dateLocale={dateLocale}
                voterCount={
                  dayVoterCounts.has(day.id) ? dayVoterCounts.get(day.id)! : undefined
                }
                totalVoters={totalVoters}
                onSelect={selectDay}
              />
            ))}
          </div>
        )}

        <div className="mt-3 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm text-muted-foreground">
              {t("v2.home.hoursLabel")} · {formatDayName(selectedDay.day, dateLocale)}{" "}
              {formatDayDate(selectedDay.day, dateLocale)}
            </span>
            <span className="text-sm text-muted-foreground">
              {[...mySlotIds].filter((id) =>
                selectedDay.slots?.some((s) => s.id === id)
              ).length > 0
                ? t("v2.home.slotsSelected", {
                    count: [...mySlotIds].filter((id) =>
                      selectedDay.slots?.some((s) => s.id === id)
                    ).length,
                  })
                : null}
            </span>
          </div>
          <PollHourStrip
            hours={playerHours}
            selectedHour={null}
            onSelectHour={() => {}}
            onToggleHour={toggleHour}
            isHourSelected={(hour) => {
              const slotId = slotIdForHour(poll, selectedDay.id, hour);
              return slotId ? mySlotIds.has(slotId) : false;
            }}
            isHourSuggested={(hour) => {
              const slotId = slotIdForHour(poll, selectedDay.id, hour);
              return slotId ? suggested.has(slotId) : false;
            }}
          />
        </div>

        <div className="mt-4 space-y-2 border-t border-border/60 pt-4">
          {isDirty ? (
            <p className="text-sm text-muted-foreground">{t("v2.home.pollVotesUnsaved")}</p>
          ) : mySlotIds.size > 0 ? (
            <p className="flex items-center gap-2 text-sm font-medium text-soccer-primary">
              <FaCheckCircle className="h-4 w-4 shrink-0" aria-hidden />
              {t("v2.home.pollVotesSaved")}
            </p>
          ) : null}
          <Button
            type="button"
            className="min-h-[48px] w-full bg-soccer-primary font-bold text-black hover:bg-soccer-primary/90"
            disabled={!isDirty || savingVotes || mySlotIds.size === 0}
            onClick={handleSaveVotes}
          >
            {savingVotes ? t("common.loading") : t("v2.home.savePollVotes")}
          </Button>
          {!isDirty && mySlotIds.size > 0 ? (
            <p className="text-center text-xs text-muted-foreground">
              {t("v2.home.pollVotesSavedDesc")}
            </p>
          ) : null}
        </div>
      </HomePhaseShell>
      {isAdmin && adminSheet}
    </>
  );
}
